import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { StoreApi } from 'zustand/vanilla';

/**
 * Adapter para usar Zustand en Angular
 * Convierte los stores de Zustand a Observables de RxJS
 * 
 * Esta clase debe ser extendida por stores específicos que crean su propio store de Zustand
 */
export class ZustandAdapter<T> {
  protected store: StoreApi<T>;
  private stateSubject: BehaviorSubject<T>;
  private unsubscribe: (() => void) | null = null;

  constructor(store: StoreApi<T>) {
    this.store = store;

    // Inicializar el BehaviorSubject con el estado actual
    this.stateSubject = new BehaviorSubject<T>(this.store.getState());

    // Suscribirse a cambios del store de Zustand y actualizar el BehaviorSubject
    this.unsubscribe = this.store.subscribe((state) => {
      this.stateSubject.next(state);
    });
  }

  /**
   * Obtiene el estado actual del store
   */
  getState(): T {
    return this.store.getState();
  }

  /**
   * Actualiza el estado del store usando la función setState de Zustand
   */
  setState(partial: Partial<T> | ((state: T) => Partial<T>)): void {
    const currentState = this.store.getState();
    const newState = typeof partial === 'function' 
      ? { ...currentState, ...partial(currentState) }
      : { ...currentState, ...partial };
    
    this.store.setState(newState as any);
  }

  /**
   * Obtiene un Observable del estado completo
   */
  get state$(): Observable<T> {
    return this.stateSubject.asObservable().pipe(
      distinctUntilChanged()
    );
  }

  /**
   * Obtiene un Observable de una parte específica del estado usando un selector
   */
  select<K>(selector: (state: T) => K): Observable<K> {
    return this.state$.pipe(
      map(selector),
      distinctUntilChanged()
    );
  }

  /**
   * Suscribe una función a cambios del store
   */
  subscribe(listener: (state: T) => void): () => void {
    return this.store.subscribe(listener);
  }

  /**
   * Obtiene el store de Zustand directamente (útil para casos avanzados)
   */
  getStore(): StoreApi<T> {
    return this.store;
  }

  /**
   * Limpia las suscripciones (llamar manualmente si es necesario)
   */
  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }
}
